'use client';

import { useCallback } from 'react';
import Link from 'next/link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, IconButton, Typography } from '@mui/material';

import { colors } from '@/styles/theme';

import { ROUTES } from '@/constants';

interface UserProfileHeaderProps {
  user: any;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

export const UserProfileHeader = ({ user, onClose, onNavigate }: UserProfileHeaderProps) => {
  const handleProfileClick = useCallback(() => {
    onNavigate(ROUTES.PROFILE);
  }, [onNavigate]);

  const handleCopyAddress = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (user?.proxyWalletAddress) {
        navigator.clipboard.writeText(user.proxyWalletAddress);
      }
    },
    [user],
  );

  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', mb: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${colors.accent.purple[500]} 0%, #e8b046 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: '#ffffff' }}>
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={handleProfileClick}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'text.primary',
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user?.name || user?.email || user?.phone || 'User'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.email || user?.phone || ''}
            </Typography>

            {user?.proxyWalletAddress && (
              <IconButton
                size="small"
                onClick={handleCopyAddress}
                sx={{ width: 20, height: 20, color: 'text.secondary' }}
              >
                <ContentCopyIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            )}
          </Box>
        </Box>
        <Link href={ROUTES.PROFILE} onClick={onClose}>
          <IconButton
            component="span"
            size="small"
            sx={{ width: 32, height: 32, color: 'text.secondary' }}
          >
            <SettingsIcon sx={{ fontSize: '1.125rem' }} />
          </IconButton>
        </Link>
      </Box>
    </Box>
  );
};
